import React, {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useDispatch} from 'react-redux'
import {useFormik} from 'formik'
import {toAbsoluteUrl} from '../../../_metronic/helpers'
import {checkUserAccess, discoverTenantSettingsAccount} from '../auth/redux/AuthCRUD'
import * as auth from '../auth/redux/AuthRedux'
import {ProjectGuestPortalClient} from '../../infrastracture/api/ProjectGuestPortalClient'

enum FormSteps {
  Credentials,
  Otp,
}

const initialValues = {
  email: '',
  password: '',
  code: '',
  shortCode: '',
}

const loginSchema = Yup.object().shape({
  email: Yup.string().email('Wrong email format').required('Email is required'),
  // Required, not a convenience: the account is resolved from this and nothing else.
  shortCode: Yup.string().trim().required('Company short code is required'),
})

export function ProjectGuestLogin() {
  const dispatch = useDispatch()
  const [step, setStep] = useState<FormSteps>(FormSteps.Credentials)
  const [email, setEmail] = useState('')
  const [resendMessage, setResendMessage] = useState('')

  // Everything past the point the short code has resolved the account: the two access checks
  // and the sign-in that hands off to the OTP step.
  const signIn = async (
    values: typeof initialValues,
    setStatus: (status: any) => void
  ) => {
    const access = await checkUserAccess(
      values.email,
      localStorage.getItem('tenantAccountId') || undefined
    )

    if (access.suspended) {
      setStatus('Your account has been suspended')
      return
    }

    if (!access.hasProjectPortalAccess) {
      setStatus('This account does not have access to the project portal')
      return
    }

    const result = await ProjectGuestPortalClient.LogIn(values.email, values.password)

    if (!result.successful) {
      setStatus('Incorrect login details')
      return
    }

    setEmail(values.email)
    setStep(FormSteps.Otp)
  }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setStatus(undefined)
      setResendMessage('')

      try {
        if (step === FormSteps.Credentials) {
          if (!values.password) return

          // The account comes from the short code and nothing else: /discovery/lookup resolves
          // it outright, where discovery by email address only answers for accounts that have
          // automatic tenant discovery turned on. The invite email carries the code.
          let discovery: {ok: boolean; data: any}

          try {
            discovery = await discoverTenantSettingsAccount(values.shortCode.trim())
          } catch (error) {
            // A short code matching no account answers 500 rather than a clean failure, so a
            // typo lands here. Reported as a not-found below rather than as a crash.
            console.error(error)
            discovery = {ok: false, data: null}
          }

          if (!discovery.ok) {
            setStatus('We could not find an account with that short code')
            return
          }

          localStorage.setItem('tenantAccountId', discovery.data.accountId)
          await signIn(values, setStatus)

          return
        }

        if (!values.code) return

        const result = await ProjectGuestPortalClient.VerifyOtp(email, values.code)
        if (!result.successful) {
          setStatus('Incorrect or expired code')
        } else {
          // Not auth.actions.login(): that triggers the CRM saga's getUserByToken(), which
          // hits /users/by-token — not on the guest allowlist (ProjectGuestRoutePolicy only admits
          // ProjectsController). /projects/me/profile is the guest-safe equivalent, so fetch
          // that directly and store it with setUser (bypasses the saga entirely).
          const profileResponse = await ProjectGuestPortalClient.GetMyProfile()
          if (profileResponse.successful) {
            // Read by ApiResponse.Create's session-expiry handler (ApiClient.ts) so a guest
            // whose 30-minute cookie expires gets redirected to /projects/guest/login instead
            // of the CRM's /login. Cleared along with everything else by LogOut()'s
            // localStorage.clear() on both explicit and automatic logout.
            localStorage.setItem('is-project-guest', 'true')
            dispatch(auth.actions.setUser(profileResponse.data))
          } else {
            setStatus('Signed in, but could not load your profile. Please try again.')
          }
        }
      } catch (error) {
        // A short code matching no account answers 500 rather than a clean failure, and the
        // discovery helpers surface that as a rejected promise. Caught so the form recovers
        // instead of leaving the button stuck on "Please wait...".
        console.error(error)
        setStatus('Something went wrong. Please try again.')
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleResend = async () => {
    setResendMessage('')
    const result = await ProjectGuestPortalClient.ResendOtp(email)
    setResendMessage(result.successful ? 'A new code has been sent.' : 'Could not resend the code.')
  }

  return (
    <div className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'>
      <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
        <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <form
            className='form w-100'
            noValidate
            id='kt_project_guest_login_form'
            onSubmit={formik.handleSubmit}
          >
            <div className='text-center mb-10'>
              <img
                alt='Logo'
                className='h-150px logo mb-5'
                src={toAbsoluteUrl('/media/logos/daysee_logo.png')}
              />
              <h1 className='text-dark mb-3'>Project Guest Sign In</h1>
              <div className='text-gray-400 fw-bold fs-4'>
                {step === FormSteps.Credentials
                  ? 'Sign in to your project portal'
                  : `Enter the code sent to ${email}`}
              </div>
            </div>

            {formik.status && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}

            {step === FormSteps.Credentials ? (
              <>
                <div className='fv-row mb-10'>
                  <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
                  <input
                    placeholder='Email'
                    {...formik.getFieldProps('email')}
                    className={clsx('form-control form-control-lg form-control-solid', {
                      'is-invalid': formik.touched.email && formik.errors.email,
                      'is-valid': formik.touched.email && !formik.errors.email,
                    })}
                    type='email'
                    name='email'
                    autoComplete='off'
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className='fv-plugins-message-container text-danger'>
                      <span role='alert'>{formik.errors.email}</span>
                    </div>
                  )}
                </div>
                <div className='fv-row mb-10'>
                  <label className='form-label fs-6 fw-bolder text-dark'>Password</label>
                  <input
                    placeholder='Password'
                    type='password'
                    autoComplete='off'
                    {...formik.getFieldProps('password')}
                    className={clsx('form-control form-control-lg form-control-solid', {
                      'is-invalid': formik.submitCount > 0 && !formik.values.password,
                    })}
                  />
                </div>

                {/* Same field, label and placeholder as the CRM login. Required here rather
                    than a fallback: this is what /discovery/lookup resolves the account from,
                    and the invite email carries it. */}
                <div className='fv-row mb-10'>
                  <label className='form-label fs-6 fw-bolder text-dark required'>
                    Company Short Code
                  </label>
                  <input
                    placeholder='Please enter your company short code'
                    type='text'
                    autoComplete='off'
                    {...formik.getFieldProps('shortCode')}
                    className={clsx('form-control form-control-lg form-control-solid', {
                      'is-invalid': formik.touched.shortCode && formik.errors.shortCode,
                      'is-valid': formik.touched.shortCode && !formik.errors.shortCode,
                    })}
                  />
                  {formik.touched.shortCode && formik.errors.shortCode && (
                    <div className='fv-plugins-message-container text-danger'>
                      <span role='alert'>{formik.errors.shortCode}</span>
                    </div>
                  )}
                </div>

                <div className='fv-row mb-5'>
                  <Link to='/projects/guest/forgot-password' className='link-primary fs-6 fw-bolder'>
                    Forgot Password?
                  </Link>
                </div>
              </>
            ) : (
              <div className='fv-row mb-10'>
                <label className='form-label fs-6 fw-bolder text-dark'>Sign-in code</label>
                <input
                  placeholder='6-digit code'
                  {...formik.getFieldProps('code')}
                  className={clsx('form-control form-control-lg form-control-solid', {
                    'is-invalid': formik.submitCount > 0 && !formik.values.code,
                  })}
                  type='text'
                  autoComplete='off'
                />
                <div className='mt-3'>
                  <a
                    href='#'
                    className='link-primary fs-7'
                    onClick={(e) => {
                      e.preventDefault()
                      handleResend()
                    }}
                  >
                    Resend code
                  </a>
                  {resendMessage && <div className='text-muted fs-8 mt-1'>{resendMessage}</div>}
                </div>
              </div>
            )}

            <div className='text-center'>
              <button
                type='submit'
                id='kt_project_guest_login_submit'
                className='btn btn-lg btn-primary w-100 mb-5'
                disabled={formik.isSubmitting}
              >
                {!formik.isSubmitting && (
                  <span className='indicator-label'>
                    {step === FormSteps.Credentials ? 'Continue' : 'Sign In'}
                  </span>
                )}
                {formik.isSubmitting && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
